import React, { useState } from 'react';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;
    width?: number | string;
    height?: number | string;
    fallbackExtensions?: string[];
    webpSrcSet?: string; // Specific webp source set if needed (e.g. for high-DPI)
    fetchPriority?: 'high' | 'low' | 'auto';
}

/**
 * OptimizedImage component that prioritizes WebP and tries fallbacks on error.
 * It also handles stripping <picture> source tags if a hard error occurs.
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
    src,
    alt,
    width,
    height,
    loading = 'lazy',
    fallbackExtensions = ['.webp', '.webp', '.webp'],
    className,
    webpSrcSet,
    fetchPriority,
    ...props
}) => {
    const [currentSrc, setCurrentSrc] = useState(src);
    const [triedFallbacks, setTriedFallbacks] = useState<string[]>([]);
    const [failedAsPicture, setFailedAsPicture] = useState(false);

    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.currentTarget;

        // If we haven't tried stripping picture sources yet, do that first
        if (!failedAsPicture) {
            setFailedAsPicture(true);
            const picture = target.parentElement;
            if (picture && picture.tagName === 'PICTURE') {
                const sources = picture.querySelectorAll('source');
                sources.forEach(s => s.remove());
            }
            // Keep the same src but force reload without source tags
            target.src = currentSrc;
            return;
        }

        // Try fallbacks if stripping picture didn't help
        const baseWithoutExt = currentSrc.replace(/\.[^/.]+$/, "");

        for (const ext of fallbackExtensions) {
            if (!triedFallbacks.includes(ext)) {
                const nextFallback = baseWithoutExt + ext;
                if (nextFallback !== currentSrc) {
                    setTriedFallbacks(prev => [...prev, ext]);
                    setCurrentSrc(nextFallback);
                    return;
                }
            }
        }
    };

    return (
        <picture className="block">
            {webpSrcSet && (
                <source
                    srcSet={webpSrcSet}
                    type="image/webp"
                />
            )}
            <img
                src={currentSrc}
                alt={alt}
                width={width}
                height={height}
                loading={loading}
                fetchPriority={fetchPriority}
                decoding="async"
                className={className}
                onError={handleError}
                style={{
                    aspectRatio: width && height ? `${width} / ${height}` : undefined,
                    ...props.style
                }}
                {...props}
            />
        </picture>
    );
};

export default OptimizedImage;
